/**
 * contactConfig.ts
 *
 * Shared configuration for contact behaviour per client entity type.
 * Used in ClientModal, MeetingScheduler, and anywhere contacts are created
 * or linked in the context of a client.
 */

export interface EntityContactConfig {
  /** Maximum number of contacts that can be linked during creation */
  maxContacts: number;
  /** Default relationship type pre-selected in the form */
  defaultRelationship: string;
  /** Ordered list of allowed relationship types for this entity */
  relationships: string[];
  /** Whether the contact name should be pre-filled from the client name */
  prefillName: boolean;
  /** Short hint shown above the contact form */
  hint: string;
}

export const ENTITY_CONTACT_CONFIG: Record<string, EntityContactConfig> = {
  Individual: {
    maxContacts: 1,
    defaultRelationship: "Self",
    relationships: ["Self", "Spouse", "Partner", "Power of Attorney", "Other"],
    prefillName: true,
    hint: "For an individual client, the contact is pre-filled from the client name.",
  },
  SoleTrader: {
    maxContacts: 1,
    defaultRelationship: "Self",
    relationships: ["Self", "Business Partner", "Power of Attorney", "Other"],
    prefillName: true,
    hint: "Add the sole trader as the primary contact.",
  },
  Company: {
    maxContacts: 20,
    defaultRelationship: "Director",
    relationships: ["Director", "Business Partner", "Trustee", "Power of Attorney", "Other"],
    prefillName: false,
    hint: "Add directors, business partners, or other key company contacts.",
  },
  Family: {
    maxContacts: 20,
    defaultRelationship: "Spouse",
    relationships: ["Spouse", "Partner", "Child", "Parent", "Sibling", "Other"],
    prefillName: false,
    hint: "Add the family members you want to link to this client.",
  },
  Trust: {
    maxContacts: 20,
    defaultRelationship: "Trustee",
    relationships: ["Trustee", "Beneficiary", "Power of Attorney", "Other"],
    prefillName: false,
    hint: "Add trustees, beneficiaries, and other trust-related contacts.",
  },
};

/** Fallback config used when entityType is unknown or not in the map */
export const DEFAULT_CONTACT_CONFIG: EntityContactConfig = {
  maxContacts: 20,
  defaultRelationship: "Other",
  relationships: [
    "Self", "Spouse", "Partner", "Child", "Parent", "Sibling",
    "Business Partner", "Director", "Trustee", "Beneficiary",
    "Power of Attorney", "Other",
  ],
  prefillName: false,
  hint: "Add contacts to link to this client.",
};

/** Returns the config for the given entity type, falling back to DEFAULT_CONTACT_CONFIG. */
export const getContactConfig = (entityType?: string): EntityContactConfig =>
  (entityType && ENTITY_CONTACT_CONFIG[entityType]) || DEFAULT_CONTACT_CONFIG;

/**
 * Splits a full name string into firstName and surname.
 * "John David Smith" → { firstName: "John", surname: "David Smith" }
 */
export const splitClientName = (fullName: string): { firstName: string; surname: string } => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], surname: "" };
  return { firstName: parts[0], surname: parts.slice(1).join(" ") };
};
